<template>
	<CardComponent :bordered="true" padding="md">
		<form class="space-y-6">
			<!-- Pending Information Section -->
			<div>
				<div class="flex items-center justify-between mb-4">
					<h3 class="text-lg font-semibold text-text-primary">
						Pending Information
						<span
							class="ml-2 text-sm font-medium text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full"
						>
							{{ pendingFields.length }}
						</span>
					</h3>
				</div>

				<div
					v-if="pendingFields.length === 0"
					class="text-center py-8 text-text-secondary"
				>
					All required information has been provided
				</div>

				<div v-else class="grid grid-cols-1 md:grid-cols-2 gap-6">
					<div v-for="field in pendingFields" :key="field.key">
						<label
							class="block text-sm font-medium text-text-primary mb-2"
						>
							{{ field.label }}
							<span v-if="field.required" class="text-red-500"
								>*</span
							>
						</label>

						<input
							v-if="field.type !== 'select'"
							:type="field.type || 'text'"
							v-model="field.value"
							:placeholder="field.placeholder || ''"
							class="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
							:required="field.required"
						/>

						<select
							v-else
							v-model="field.value"
							class="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
							:required="field.required"
						>
							<option value="" disabled>Select option</option>
							<option
								v-for="opt in field.options"
								:key="opt"
								:value="opt"
							>
								{{ opt }}
							</option>
						</select>
					</div>
				</div>
			</div>

			<!-- Provided Information Section -->
			<div class="pt-6 border-t border-border">
				<div class="flex items-center justify-between mb-4">
					<h3 class="text-lg font-semibold text-text-primary">
						Provided Information
						<span
							class="ml-2 text-sm font-medium text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full"
						>
							{{ providedFields.length }}
						</span>
					</h3>
				</div>

				<div
					v-if="providedFields.length === 0"
					class="text-center py-8 text-text-secondary"
				>
					No information provided yet
				</div>

				<div v-else class="grid grid-cols-1 md:grid-cols-2 gap-6">
					<div v-for="field in providedFields" :key="field.key">
						<label
							class="block text-sm font-medium text-text-secondary mb-2"
						>
							{{ field.label }}
						</label>
						<div
							class="w-full px-4 py-2 border border-border rounded-lg bg-gray-50 text-text-primary"
						>
							{{ field.value || '—' }}
						</div>
					</div>
				</div>
			</div>

			<!-- Original form fields (kept as they were) -->
			<div class="grid grid-cols-1 md:grid-cols-2 gap-6">
				<div>
					<label
						class="block text-sm font-medium text-text-primary mb-2"
						>Claim Form</label
					>
					<input
						type="text"
						value="Provided"
						class="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
						readonly
					/>
				</div>
				<div>
					<label
						class="block text-sm font-medium text-text-primary mb-2"
						>Detail</label
					>
					<input
						type="text"
						class="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
					/>
				</div>
			</div>

			<div class="grid grid-cols-1 md:grid-cols-3 gap-6">
				<div>
					<label
						class="block text-sm font-medium text-text-primary mb-2"
						>Visibility</label
					>
					<select
						class="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
					>
						<option>Night</option>
						<option>Day</option>
					</select>
				</div>
				<div>
					<label
						class="block text-sm font-medium text-text-primary mb-2"
						>Location</label
					>
					<input
						type="text"
						value="Nakuru"
						class="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
					/>
				</div>
				<div>
					<label
						class="block text-sm font-medium text-text-primary mb-2"
						>No. Passenger</label
					>
					<input
						type="number"
						value="3"
						class="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
					/>
				</div>
			</div>

			<!-- Images Section -->
			<div class="pt-6 border-t border-border">
				<h3 class="text-lg font-semibold text-text-primary mb-4">
					Images
				</h3>
				<div class="relative">
					<div class="overflow-hidden rounded-lg">
						<div
							class="aspect-video bg-gray-100 flex items-center justify-center"
						>
							<img
								:src="claimImages[currentImageIndex]"
								:alt="`Claim image ${currentImageIndex + 1}`"
								class="w-full h-full object-cover"
							/>
						</div>
					</div>

					<button
						@click="$emit('previous-image')"
						type="button"
						class="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-colors"
						:disabled="currentImageIndex === 0"
						:class="{
							'opacity-50 cursor-not-allowed':
								currentImageIndex === 0,
						}"
					>
						<i class="fa fa-chevron-left text-gray-700"></i>
					</button>

					<button
						@click="$emit('next-image')"
						type="button"
						class="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-colors"
						:disabled="currentImageIndex === claimImages.length - 1"
						:class="{
							'opacity-50 cursor-not-allowed':
								currentImageIndex === claimImages.length - 1,
						}"
					>
						<i class="fa fa-chevron-right text-gray-700"></i>
					</button>

					<div
						class="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2"
					>
						<button
							v-for="(image, index) in claimImages"
							:key="index"
							@click="updateCurrentImageIndex(index)"
							type="button"
							class="w-2 h-2 rounded-full transition-colors"
							:class="
								index === currentImageIndex
									? 'bg-white'
									: 'bg-white/50'
							"
						></button>
					</div>

					<div
						class="absolute top-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm"
					>
						{{ currentImageIndex + 1 }} / {{ claimImages.length }}
					</div>
				</div>

				<div class="grid grid-cols-4 md:grid-cols-6 gap-2 mt-4">
					<div
						v-for="(image, index) in claimImages"
						:key="index"
						@click="updateCurrentImageIndex(index)"
						class="aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all"
						:class="
							index === currentImageIndex
								? 'border-primary'
								: 'border-transparent hover:border-gray-300'
						"
					>
						<img
							:src="image"
							:alt="`Thumbnail ${index + 1}`"
							class="w-full h-full object-cover"
						/>
					</div>
				</div>
			</div>

			<!-- Documents Section -->
			<div class="pt-6 border-t border-border">
				<h3 class="text-lg font-semibold text-text-primary mb-4">
					Documents
				</h3>
				<div class="space-y-3">
					<div
						class="flex items-center justify-between p-4 bg-surface rounded-lg"
					>
						<div class="flex items-center gap-3">
							<i class="fa fa-file-pdf text-2xl text-red-500"></i>
							<div>
								<p class="font-medium text-text-primary">
									Driving License
								</p>
								<p class="text-xs text-text-secondary">
									2.4 MB
								</p>
							</div>
						</div>
						<button
							type="button"
							class="px-4 py-2 text-sm text-primary hover:bg-blue-50 rounded transition-colors"
						>
							Download
						</button>
					</div>
					<div
						class="flex items-center justify-between p-4 bg-surface rounded-lg"
					>
						<div class="flex items-center gap-3">
							<i class="fa fa-file-pdf text-2xl text-red-500"></i>
							<div>
								<p class="font-medium text-text-primary">
									Police Abstract
								</p>
								<p class="text-xs text-text-secondary">
									1.8 MB
								</p>
							</div>
						</div>
						<button
							type="button"
							class="px-4 py-2 text-sm text-primary hover:bg-blue-50 rounded transition-colors"
						>
							Download
						</button>
					</div>
				</div>
			</div>
		</form>
	</CardComponent>
</template>

<script>
import CardComponent from '../Card.vue';

export default {
	name: 'ClaimFormTab',
	components: {
		CardComponent,
	},
	props: {
		damageImages: {
			type: Array,
			default: () => [],
		},
		currentImageIndex: {
			type: Number,
			default: 0,
		},
	},
	data() {
		return {
			pendingFields: [
				{
					key: 'detail',
					label: 'Detail',
					type: 'text',
					value: '',
					required: false,
				},
				{
					key: 'visibility',
					label: 'Visibility',
					type: 'select',
					value: '',
					options: ['Night', 'Day'],
					required: false,
				},
				{
					key: 'passengers',
					label: 'No. Passenger',
					type: 'number',
					value: 3,
					required: false,
				},
			],
			providedFields: [
				{
					key: 'claim_form',
					label: 'Claim Form',
					value: 'Provided',
				},
			],
		};
	},
	computed: {
		claimImages() {
			const images = this.damageImages || [];
			return images.length > 0
				? images
				: [
						'https://placehold.co/800x600/3B82F6/FFFFFF/png?text=Front+View',
						'https://placehold.co/800x600/EF4444/FFFFFF/png?text=Rear+Damage',
						'https://placehold.co/800x600/10B981/FFFFFF/png?text=Side+Impact',
						'https://placehold.co/800x600/F59E0B/FFFFFF/png?text=Dashboard',
						'https://placehold.co/800x600/8B5CF6/FFFFFF/png?text=Documents',
						'https://placehold.co/800x600/EC4899/FFFFFF/png?text=License+Plate',
					];
		},
	},
	methods: {
		updateCurrentImageIndex(index) {
			this.$emit('update:current-image-index', index);
		},
	},
};
</script>

<style scoped></style>
